import React, { useState, useEffect, useCallback } from 'react';
import {
  Fingerprint, Loader2, AlertCircle, CheckCircle, Users, UserPlus,
  CreditCard, KeyRound, Lock, Timer, Monitor, Check, Nfc, ScanLine, Power,
  AlertTriangle, Siren, ShieldCheck, Clock, FileCheck2, Ban, Plus, X,
} from 'lucide-react';
import { badgeApi } from '../utils/api';

/* ── shared styling (mirrors Compliance.jsx / portal palette) ───────── */

const cardStyle = {
  background: '#fff', border: '1px solid #eee', borderRadius: 10,
  padding: '16px 18px', boxShadow: '0 1px 3px rgba(0,0,0,.05)',
};

const inputStyle = {
  padding: '8px 10px', border: '1px solid #ddd', borderRadius: 8,
  fontSize: 13.5, font: 'inherit', outline: 'none', background: '#fff',
};

const primaryBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  background: '#068dac', color: '#fff', border: 'none', borderRadius: 8,
  padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
};

const outlineBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  background: 'none', border: '1px solid #068dac', borderRadius: 8,
  color: '#068dac', padding: '7px 12px', fontSize: 12.5, fontWeight: 600,
  cursor: 'pointer',
};

const dangerOutlineBtn = {
  ...outlineBtn, border: '1px solid #d9b0b0', color: '#b91c1c',
};

const sectionHeading = (Icon, label) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
    <Icon size={17} color="#068dac" />
    <div style={{ fontSize: 15, fontWeight: 700 }}>{label}</div>
  </div>
);

const thStyle = {
  textAlign: 'left', fontSize: 11.5, fontWeight: 700, color: '#888',
  textTransform: 'uppercase', letterSpacing: 0.4, padding: '6px 10px 6px 0',
  borderBottom: '1px solid #eee', whiteSpace: 'nowrap',
};

const tdStyle = {
  fontSize: 13, color: '#333', padding: '8px 10px 8px 0',
  borderBottom: '1px solid #f5f5f5', verticalAlign: 'top',
};

function Chip({ label, color, bg, icon: Icon }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 9px', borderRadius: 999, fontSize: 11.5, fontWeight: 600,
      color, background: bg, whiteSpace: 'nowrap',
    }}>
      {Icon && <Icon size={12} />}
      {label}
    </span>
  );
}

const GREEN = { color: '#16a34a', bg: '#dcfce7' };
const AMBER = { color: '#b45309', bg: '#fef3c7' };
const RED = { color: '#b91c1c', bg: '#fee2e2' };
const GRAY = { color: '#6b7280', bg: '#f3f4f6' };

function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch (e) { return String(iso); }
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (e) { return String(iso); }
}

function relTime(iso) {
  if (!iso) return 'never';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return String(iso);
  const mins = Math.floor((Date.now() - t) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  return fmtDate(iso);
}

// Session rows may arrive with person_* (v2 schema) — tolerate legacy keys.
const personName = (row) => row.person_name || row.employee_name || 'Unknown';

// Roster rows: v3 may return a single `name` or first/last parts.
const personDisplay = (p) =>
  p.name
  || [p.first_name || p.first, p.last_name || p.last].filter(Boolean).join(' ')
  || 'Unknown';

// Workstation rows are the customer's MNMON deployments — the deployment id
// may arrive as `deployment_id` or plain `id`.
const wsId = (w) => w.deployment_id || w.id;

function SectionLoading() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#888', padding: '8px 0' }}>
      <Loader2 size={14} className="spinner" />
      Loading…
    </div>
  );
}

function SectionError({ message, onRetry }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#b91c1c', padding: '8px 0' }}>
      <AlertCircle size={14} />
      <span>{message}</span>
      {onRetry && (
        <button onClick={onRetry} style={{ ...outlineBtn, padding: '4px 10px', fontSize: 12 }}>
          Retry
        </button>
      )}
    </div>
  );
}

function EmptyNote({ children }) {
  return <div style={{ fontSize: 13, color: '#888', padding: '6px 0' }}>{children}</div>;
}

// Two-step destructive button: first click arms it, second click fires.
function ConfirmButton({ label, confirmLabel, onConfirm, busy }) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return undefined;
    const t = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(t);
  }, [armed]);
  return (
    <button
      onClick={() => {
        if (busy) return;
        if (!armed) { setArmed(true); return; }
        setArmed(false);
        onConfirm();
      }}
      disabled={busy}
      style={{
        ...dangerOutlineBtn,
        ...(armed ? { background: '#fee2e2', borderColor: '#b91c1c' } : {}),
        opacity: busy ? 0.6 : 1,
        cursor: busy ? 'default' : 'pointer',
      }}
    >
      {busy ? <Loader2 size={12} className="spinner" /> : null}
      {armed ? (confirmLabel || 'Confirm?') : label}
    </button>
  );
}

// PIN pair with double-entry validation, shared by assign-card and reset-PIN.
function PinFields({ pin, setPin, pin2, setPin2 }) {
  return (
    <>
      <input
        type="password"
        value={pin}
        onChange={(e) => setPin(e.target.value)}
        placeholder="PIN (4–12 characters)"
        autoComplete="new-password"
        maxLength={12}
        style={{ ...inputStyle, width: 160 }}
      />
      <input
        type="password"
        value={pin2}
        onChange={(e) => setPin2(e.target.value)}
        placeholder="Re-enter PIN"
        autoComplete="new-password"
        maxLength={12}
        style={{ ...inputStyle, width: 160 }}
      />
    </>
  );
}

function validatePinPair(pin, pin2) {
  if (pin.length < 4 || pin.length > 12) return 'PIN must be 4–12 characters.';
  if (pin !== pin2) return 'The two PIN entries do not match.';
  return null;
}

/* ── Scan-to-register modal ─────────────────────────────────────────── */
/*
 * V3 flow: card UIDs aren't printed on the cards, so the UID is captured by
 * tapping the card on one of the practice's own (MNMON-managed) workstations
 * while a ~2 minute "registration mode" is armed:
 *   details -> arm (POST registration/arm {deployment_id})
 *           -> waiting (poll GET registration/status every ~2s)
 *           -> pin (captured_uid arrived; set the PIN)
 *           -> saving (create person if new, then assign the card)
 * mode="new"      — First/Last/Employee ID fields, creates the person too.
 * mode="existing" — pick an existing active person, just adds the card.
 */

const REG_WINDOW_SECONDS = 120;
const REG_POLL_MS = 2000;

function ScanRegisterModal({ mode, persons, workstations, wsState, onDone, onCancel }) {
  const [step, setStep] = useState('details'); // details | waiting | pin | saving
  const [error, setError] = useState(null);

  // details
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [personId, setPersonId] = useState('');       // existing mode
  const [deploymentId, setDeploymentId] = useState('');
  const [arming, setArming] = useState(false);

  // waiting
  const [expiresAt, setExpiresAt] = useState(null);
  const [remaining, setRemaining] = useState(REG_WINDOW_SECONDS);

  // pin / saving
  const [capturedUid, setCapturedUid] = useState(null);
  const [pin, setPin] = useState('');
  const [pin2, setPin2] = useState('');
  // If person creation succeeded but the card assign failed, remember the
  // person so a retry doesn't create a duplicate.
  const [createdPersonId, setCreatedPersonId] = useState(null);

  const activePersons = (persons || []).filter((p) => p.active !== false);
  const console_ = workstations.find((w) => String(wsId(w)) === String(deploymentId));

  const arm = async () => {
    setError(null);
    if (mode === 'new') {
      if (!first.trim() || !last.trim()) { setError('First and last name are required.'); return; }
    } else if (!personId) {
      setError('Choose who this card belongs to.');
      return;
    }
    if (!deploymentId) { setError('Choose the workstation you will tap the card on.'); return; }
    setArming(true);
    try {
      const d = await badgeApi.armRegistration(deploymentId);
      const exp = d && d.expires_at ? Date.parse(d.expires_at) : NaN;
      const expMs = Number.isNaN(exp) ? Date.now() + REG_WINDOW_SECONDS * 1000 : exp;
      setExpiresAt(expMs);
      setRemaining(Math.max(0, Math.round((expMs - Date.now()) / 1000)));
      setStep('waiting');
    } catch (e) {
      setError(e.message || 'Failed to arm the card scan.');
    } finally {
      setArming(false);
    }
  };

  // Poll for the captured UID while waiting; expire the window client-side
  // (the server expires the pending row on its own schedule).
  useEffect(() => {
    if (step !== 'waiting' || !expiresAt) return undefined;
    let stopped = false;

    const tick = setInterval(() => {
      const left = Math.max(0, Math.round((expiresAt - Date.now()) / 1000));
      setRemaining(left);
      if (left <= 0 && !stopped) {
        stopped = true;
        setStep('details');
        setError('The scan window expired before a card was tapped — arm it again when you are ready.');
      }
    }, 1000);

    const poll = setInterval(async () => {
      if (stopped) return;
      try {
        const s = await badgeApi.getRegistrationStatus();
        if (stopped) return;
        if (s && s.captured_uid) {
          stopped = true;
          setCapturedUid(s.captured_uid);
          setStep('pin');
        } else if (s && s.armed === false) {
          // Server-side the window is gone (expired or superseded).
          stopped = true;
          setStep('details');
          setError('The scan window is no longer armed — arm it again when you are ready.');
        }
      } catch (e) {
        // Transient poll failures are fine — keep polling until the window
        // runs out.
      }
    }, REG_POLL_MS);

    return () => { stopped = true; clearInterval(tick); clearInterval(poll); };
  }, [step, expiresAt]);

  const save = async () => {
    const pinErr = validatePinPair(pin, pin2);
    if (pinErr) { setError(pinErr); return; }
    setError(null);
    setStep('saving');
    try {
      let pid = personId || createdPersonId;
      if (mode === 'new' && !pid) {
        const d = await badgeApi.addPerson({
          first: first.trim(),
          last: last.trim(),
          employee_id: employeeId.trim() || undefined,
        });
        pid = d.id || (d.person && d.person.id) || d.person_id;
        if (!pid) throw new Error('The employee was created but no id came back — reload and try assigning the card again.');
        setCreatedPersonId(pid);
      }
      await badgeApi.assignCard(pid, capturedUid, pin);
      onDone(mode === 'new'
        ? `${first.trim()} ${last.trim()} added — card registered and PIN set.`
        : 'Card registered and PIN set.');
    } catch (e) {
      setError(e.message || 'Failed to save.');
      setStep('pin');
    }
  };

  const modalLabel = mode === 'new' ? 'Add employee' : 'Register a card';

  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,.4)', zIndex: 60,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 12, width: '100%', maxWidth: 460,
          maxHeight: '86vh', overflowY: 'auto', padding: '18px 20px',
          boxShadow: '0 12px 32px rgba(0,0,0,.18)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ScanLine size={17} color="#068dac" />
            <div style={{ fontSize: 15.5, fontWeight: 700 }}>{modalLabel}</div>
          </div>
          <button
            onClick={onCancel}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999', padding: 4 }}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
        <div style={{ fontSize: 12.5, color: '#888', marginBottom: 14, lineHeight: 1.5 }}>
          {mode === 'new'
            ? 'The card is registered by tapping it — no codes to type. Enter the employee, pick the workstation you are standing at, then tap their new card on its reader.'
            : 'Pick the person and the workstation you are standing at, then tap the new card on its reader.'}
        </div>

        {step === 'details' && (
          <>
            {mode === 'new' ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                <input
                  value={first}
                  onChange={(e) => setFirst(e.target.value)}
                  placeholder="First name"
                  style={{ ...inputStyle, flex: '1 1 140px' }}
                />
                <input
                  value={last}
                  onChange={(e) => setLast(e.target.value)}
                  placeholder="Last name"
                  style={{ ...inputStyle, flex: '1 1 140px' }}
                />
                <input
                  value={employeeId}
                  onChange={(e) => setEmployeeId(e.target.value)}
                  placeholder="Employee ID (optional)"
                  style={{ ...inputStyle, flex: '1 1 100%' }}
                />
              </div>
            ) : (
              <select
                value={personId}
                onChange={(e) => setPersonId(e.target.value)}
                style={{ ...inputStyle, width: '100%', marginBottom: 10 }}
              >
                <option value="">Card belongs to…</option>
                {activePersons.map((p) => (
                  <option key={p.id} value={p.id}>
                    {personDisplay(p)}{p.employee_id ? ` (#${p.employee_id})` : ''}
                  </option>
                ))}
              </select>
            )}

            <div style={{ fontSize: 12.5, fontWeight: 700, color: '#555', marginBottom: 6 }}>
              Registration console
            </div>
            {wsState === 'loading' && <SectionLoading />}
            {wsState === 'error' && (
              <div style={{ fontSize: 12.5, color: '#b91c1c', marginBottom: 10 }}>
                Could not load your workstations — close this and retry from the
                Workstations section.
              </div>
            )}
            {wsState === 'ready' && workstations.length === 0 && (
              <div style={{ fontSize: 12.5, color: '#888', marginBottom: 10 }}>
                No workstations found. Your Macula-managed devices appear here
                automatically — contact us if this list should not be empty.
              </div>
            )}
            {wsState === 'ready' && workstations.length > 0 && (
              <select
                value={deploymentId}
                onChange={(e) => setDeploymentId(e.target.value)}
                style={{ ...inputStyle, width: '100%', marginBottom: 12 }}
              >
                <option value="">Where will you tap the card?</option>
                {workstations.map((w) => (
                  <option key={wsId(w)} value={wsId(w)}>
                    {w.hostname || wsId(w)} — seen {relTime(w.last_seen)}
                  </option>
                ))}
              </select>
            )}

            {error && (
              <div style={{ fontSize: 12.5, color: '#b91c1c', marginBottom: 10 }}>{error}</div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={onCancel} style={{ ...outlineBtn, border: '1px solid #ccc', color: '#666' }}>
                Cancel
              </button>
              <button
                onClick={arm}
                disabled={arming || wsState !== 'ready' || workstations.length === 0}
                style={{ ...primaryBtn, opacity: arming || wsState !== 'ready' || workstations.length === 0 ? 0.6 : 1 }}
              >
                {arming ? <Loader2 size={13} className="spinner" /> : <ScanLine size={13} />}
                Arm scan
              </button>
            </div>
          </>
        )}

        {step === 'waiting' && (
          <div style={{ textAlign: 'center', padding: '10px 0 4px' }}>
            <Nfc size={36} color="#068dac" style={{ marginBottom: 10 }} />
            <div style={{ fontSize: 14.5, fontWeight: 600, marginBottom: 4 }}>
              Waiting for tap on {console_ ? (console_.hostname || 'the workstation') : 'the workstation'}…
            </div>
            <div style={{ fontSize: 12.5, color: '#888', marginBottom: 10, lineHeight: 1.5 }}>
              Hold the new card against that workstation's reader. The scan
              window closes in {remaining}s.
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 12.5, color: '#068dac', marginBottom: 14 }}>
              <Loader2 size={14} className="spinner" />
              Listening…
            </div>
            <button onClick={onCancel} style={{ ...outlineBtn, border: '1px solid #ccc', color: '#666' }}>
              Cancel
            </button>
          </div>
        )}

        {(step === 'pin' || step === 'saving') && (
          <>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px',
              background: '#dcfce7', borderRadius: 8, marginBottom: 12,
            }}>
              <CheckCircle size={16} color="#16a34a" style={{ flexShrink: 0 }} />
              <div style={{ fontSize: 13, color: '#166534' }}>
                <strong>Card detected</strong>
                <span style={{
                  marginLeft: 8, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  fontSize: 12, background: '#fff', borderRadius: 5, padding: '1px 6px',
                }}>
                  {capturedUid}
                </span>
              </div>
            </div>
            <div style={{ fontSize: 12.5, color: '#888', marginBottom: 10, lineHeight: 1.5 }}>
              Now set the PIN {mode === 'new' ? `${first.trim()} ${last.trim()}` : 'they'} will
              enter after tapping in.
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
              <PinFields pin={pin} setPin={setPin} pin2={pin2} setPin2={setPin2} />
            </div>
            {error && (
              <div style={{ fontSize: 12.5, color: '#b91c1c', marginBottom: 10 }}>{error}</div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={onCancel}
                disabled={step === 'saving'}
                style={{ ...outlineBtn, border: '1px solid #ccc', color: '#666', opacity: step === 'saving' ? 0.6 : 1 }}
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={step === 'saving'}
                style={{ ...primaryBtn, opacity: step === 'saving' ? 0.7 : 1 }}
              >
                {step === 'saving' ? <Loader2 size={13} className="spinner" /> : <Check size={13} />}
                Save
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ── Staff roster ───────────────────────────────────────────────────── */

function RosterSection({ persons, state, error, onReload, workstations, wsState }) {
  const [showAdd, setShowAdd] = useState(false);
  const [notice, setNotice] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [rowError, setRowError] = useState(null);

  const deactivate = async (id) => {
    setBusyId(id);
    setRowError(null);
    setNotice(null);
    try {
      await badgeApi.deactivatePerson(id);
      setNotice('Employee deactivated — their badge cards were deregistered and stop working immediately.');
      onReload();
    } catch (e) {
      setRowError(e.message || 'Failed to deactivate person.');
    } finally {
      setBusyId(null);
    }
  };

  const active = persons.filter((p) => p.active !== false);
  const inactive = persons.filter((p) => p.active === false);

  return (
    <div style={{ ...cardStyle, marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        {sectionHeading(Users, 'Staff roster')}
        <button onClick={() => setShowAdd(true)} style={outlineBtn}>
          <UserPlus size={13} />
          Add employee
        </button>
      </div>
      <div style={{ fontSize: 12.5, color: '#888', margin: '-6px 0 12px' }}>
        The people at your practice who badge in and out. Adding an employee
        registers their card in the same step — you tap it on one of your own
        workstations, no codes to type.
      </div>

      {showAdd && (
        <ScanRegisterModal
          mode="new"
          workstations={workstations}
          wsState={wsState}
          onDone={(msg) => { setShowAdd(false); setNotice(msg); onReload(); }}
          onCancel={() => setShowAdd(false)}
        />
      )}

      {notice && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: '#16a34a', marginBottom: 8 }}>
          <CheckCircle size={13} />
          {notice}
        </div>
      )}

      {state === 'loading' && <SectionLoading />}
      {state === 'error' && <SectionError message={error} onRetry={onReload} />}
      {state === 'ready' && (
        <>
          {rowError && (
            <div style={{ fontSize: 12.5, color: '#b91c1c', marginBottom: 8 }}>{rowError}</div>
          )}
          {active.length === 0 && inactive.length === 0 && (
            <EmptyNote>No staff on the roster yet — add your first employee above.</EmptyNote>
          )}
          {active.map((p, i) => (
            <div
              key={p.id}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                flexWrap: 'wrap', gap: 8, padding: '9px 0',
                borderTop: i === 0 ? 'none' : '1px solid #f2f2f2',
              }}
            >
              <div style={{ minWidth: 0 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: '#333' }}>{personDisplay(p)}</span>
                {p.title && (
                  <span style={{ fontSize: 12.5, color: '#888', marginLeft: 8 }}>{p.title}</span>
                )}
                {p.employee_id && (
                  <span style={{ fontSize: 12.5, color: '#888', marginLeft: 8 }}>#{p.employee_id}</span>
                )}
              </div>
              <ConfirmButton
                label="Deactivate"
                confirmLabel="Confirm — cards stop working"
                busy={busyId === p.id}
                onConfirm={() => deactivate(p.id)}
              />
            </div>
          ))}
          {inactive.length > 0 && (
            <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid #f2f2f2' }}>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>
                Deactivated (cards deregistered)
              </div>
              {inactive.map((p) => (
                <div key={p.id} style={{ fontSize: 13, color: '#999', padding: '4px 0' }}>
                  {personDisplay(p)}{p.title ? ` · ${p.title}` : ''}
                  {p.employee_id ? ` · #${p.employee_id}` : ''}
                  {p.deactivated_at ? ` · deactivated ${fmtDate(p.deactivated_at)}` : ''}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ── Cards ──────────────────────────────────────────────────────────── */

function ResetPinForm({ cardId, onDone, onCancel }) {
  const [pin, setPin] = useState('');
  const [pin2, setPin2] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    const pinErr = validatePinPair(pin, pin2);
    if (pinErr) { setError(pinErr); return; }
    setSaving(true);
    setError(null);
    try {
      await badgeApi.resetCardPin(cardId, pin);
      onDone();
    } catch (e) {
      setError(e.message || 'Failed to reset PIN.');
      setSaving(false);
    }
  };

  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center',
      padding: '10px 12px', background: '#f8fafb', borderRadius: 8, margin: '6px 0 10px',
    }}>
      <PinFields pin={pin} setPin={setPin} pin2={pin2} setPin2={setPin2} />
      <button onClick={submit} disabled={saving} style={{ ...primaryBtn, opacity: saving ? 0.7 : 1 }}>
        {saving ? <Loader2 size={13} className="spinner" /> : <KeyRound size={13} />}
        Set new PIN
      </button>
      <button onClick={onCancel} style={{ ...outlineBtn, border: '1px solid #ccc', color: '#666' }}>
        Cancel
      </button>
      {error && <div style={{ flexBasis: '100%', fontSize: 12.5, color: '#b91c1c' }}>{error}</div>}
    </div>
  );
}

function CardsSection({ cards, persons, state, error, onReload, workstations, wsState }) {
  const [showAssign, setShowAssign] = useState(false);
  const [resetId, setResetId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [rowError, setRowError] = useState(null);
  const [notice, setNotice] = useState(null);

  const deactivate = async (id) => {
    setBusyId(id);
    setRowError(null);
    try {
      await badgeApi.deactivateCard(id);
      setNotice('Card deactivated — it stops working at every workstation on the next tap.');
      onReload();
    } catch (e) {
      setRowError(e.message || 'Failed to deactivate card.');
    } finally {
      setBusyId(null);
    }
  };

  const activeCards = cards.filter((c) => c.active !== false);
  const inactiveCards = cards.filter((c) => c.active === false);

  return (
    <div style={{ ...cardStyle, marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        {sectionHeading(CreditCard, 'Badge cards')}
        <button
          onClick={() => setShowAssign(true)}
          style={outlineBtn}
          disabled={state !== 'ready'}
        >
          <Plus size={13} />
          Register a card
        </button>
      </div>
      <div style={{ fontSize: 12.5, color: '#888', margin: '-6px 0 12px' }}>
        New employees get their card in the Add-employee step. Use "Register a
        card" for a replacement or extra card — you tap it on one of your
        workstations, same as adding someone. Lost a card? Deactivate it — the
        kill is instant.
      </div>

      {showAssign && state === 'ready' && (
        <ScanRegisterModal
          mode="existing"
          persons={persons}
          workstations={workstations}
          wsState={wsState}
          onDone={(msg) => { setShowAssign(false); setNotice(msg); onReload(); }}
          onCancel={() => setShowAssign(false)}
        />
      )}

      {notice && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: '#16a34a', marginBottom: 8 }}>
          <CheckCircle size={13} />
          {notice}
        </div>
      )}
      {rowError && (
        <div style={{ fontSize: 12.5, color: '#b91c1c', marginBottom: 8 }}>{rowError}</div>
      )}

      {state === 'loading' && <SectionLoading />}
      {state === 'error' && <SectionError message={error} onRetry={onReload} />}
      {state === 'ready' && (
        <>
          {activeCards.length === 0 && inactiveCards.length === 0 && (
            <EmptyNote>
              No cards bound yet. Once Macula issues your cards, assign each one
              to a person above.
            </EmptyNote>
          )}
          {activeCards.map((c, i) => (
            <div key={c.id} style={{ borderTop: i === 0 ? 'none' : '1px solid #f2f2f2' }}>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                flexWrap: 'wrap', gap: 8, padding: '9px 0',
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#333' }}>
                    {personName(c)}
                    <span style={{
                      fontSize: 12, fontWeight: 500, color: '#666', marginLeft: 8,
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                      background: '#f3f4f6', borderRadius: 5, padding: '1px 6px',
                    }}>
                      {c.card_uid}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>
                    Assigned {fmtDate(c.created_at)}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                  {c.locked && <Chip label="Locked out" {...RED} icon={Ban} />}
                  {!c.locked && c.failed_attempts > 0 && (
                    <Chip label={`${c.failed_attempts} failed tr${c.failed_attempts === 1 ? 'y' : 'ies'}`} {...AMBER} icon={AlertCircle} />
                  )}
                  <button
                    onClick={() => setResetId(resetId === c.id ? null : c.id)}
                    style={outlineBtn}
                  >
                    <KeyRound size={12} />
                    Reset PIN
                  </button>
                  <ConfirmButton
                    label="Deactivate"
                    confirmLabel="Confirm deactivate"
                    busy={busyId === c.id}
                    onConfirm={() => deactivate(c.id)}
                  />
                </div>
              </div>
              {resetId === c.id && (
                <ResetPinForm
                  cardId={c.id}
                  onDone={() => { setResetId(null); setNotice(`PIN updated for ${personName(c)}.`); onReload(); }}
                  onCancel={() => setResetId(null)}
                />
              )}
            </div>
          ))}
          {inactiveCards.length > 0 && (
            <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid #f2f2f2' }}>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>
                Deactivated
              </div>
              {inactiveCards.map((c) => (
                <div key={c.id} style={{ fontSize: 13, color: '#999', padding: '4px 0' }}>
                  {personName(c)} · {c.card_uid}
                  {c.deactivated_at ? ` · deactivated ${fmtDate(c.deactivated_at)}` : ''}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ── Lock policy ────────────────────────────────────────────────────── */

const MODE_INFO = {
  flow: {
    label: 'Flow',
    hint: 'Tap in, tap out. The screen re-locks on tap-out or when the idle timer runs out — the everyday clinical mode.',
  },
  high_security: {
    label: 'High security',
    hint: 'The screen locks the moment the badge leaves the reader. For workstations handling the most sensitive records.',
  },
};

function PolicySection() {
  const [state, setState] = useState('loading'); // loading | ready | error
  const [error, setError] = useState(null);
  const [stored, setStored] = useState({});      // stored master policy (merge target)
  const [mode, setMode] = useState('flow');
  const [timeoutSec, setTimeoutSec] = useState(300);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  const [saveError, setSaveError] = useState(null);

  const load = useCallback(async () => {
    setState('loading');
    try {
      const d = await badgeApi.getPolicy();
      const eff = d.effective || {};
      setStored(d.policy || {});
      setMode(eff.mode || 'flow');
      setTimeoutSec(eff.idle_timeout_seconds != null ? eff.idle_timeout_seconds : 300);
      setState('ready');
    } catch (e) {
      setError(e.message || 'Failed to load lock policy.');
      setState('error');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const t = parseInt(timeoutSec, 10);
    if (Number.isNaN(t) || t < 30 || t > 3600) {
      setSaveState('error');
      setSaveError('Idle timeout must be between 30 and 3600 seconds.');
      return;
    }
    setSaveState('saving');
    setSaveError(null);
    try {
      // Merge onto the stored policy so keys we don't manage here survive.
      const d = await badgeApi.putPolicy({ ...stored, mode, idle_timeout_seconds: t });
      setStored(d.policy || {});
      setSaveState('saved');
      setTimeout(() => setSaveState((s) => (s === 'saved' ? 'idle' : s)), 2500);
    } catch (e) {
      setSaveState('error');
      setSaveError(e.message || 'Failed to save lock policy.');
    }
  };

  return (
    <div style={{ ...cardStyle, marginBottom: 14 }}>
      {sectionHeading(Lock, 'Lock policy')}
      <div style={{ fontSize: 12.5, color: '#888', margin: '-6px 0 12px' }}>
        Applies to every workstation. Changes reach the workstations
        automatically within about a minute.
      </div>

      {state === 'loading' && <SectionLoading />}
      {state === 'error' && <SectionError message={error} onRetry={load} />}
      {state === 'ready' && (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
            {Object.entries(MODE_INFO).map(([key, info]) => (
              <label
                key={key}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
                  padding: '10px 12px', borderRadius: 8,
                  border: mode === key ? '1.5px solid #068dac' : '1px solid #eee',
                  background: mode === key ? '#f0f9fb' : '#fff',
                }}
              >
                <input
                  type="radio"
                  name="badge-mode"
                  checked={mode === key}
                  onChange={() => setMode(key)}
                  style={{ marginTop: 3, accentColor: '#068dac' }}
                />
                <span>
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#333', display: 'block' }}>
                    {info.label}
                  </span>
                  <span style={{ fontSize: 12.5, color: '#777', lineHeight: 1.5 }}>{info.hint}</span>
                </span>
              </label>
            ))}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
            <Timer size={16} color="#068dac" />
            <span style={{ fontSize: 13.5, color: '#333', fontWeight: 600 }}>Idle timeout</span>
            <input
              type="number"
              min={30}
              max={3600}
              step={30}
              value={timeoutSec}
              onChange={(e) => setTimeoutSec(e.target.value)}
              style={{ ...inputStyle, width: 90 }}
            />
            <span style={{ fontSize: 12.5, color: '#888' }}>
              seconds (30–3600) with no activity before the screen re-locks
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              onClick={save}
              disabled={saveState === 'saving'}
              style={{ ...primaryBtn, opacity: saveState === 'saving' ? 0.7 : 1 }}
            >
              {saveState === 'saving' ? <Loader2 size={13} className="spinner" /> : <Check size={13} />}
              Save policy
            </button>
            {saveState === 'saved' && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, color: '#16a34a', fontWeight: 600 }}>
                <CheckCircle size={13} />
                Saved — workstations will pick it up shortly.
              </span>
            )}
            {saveState === 'error' && saveError && (
              <span style={{ fontSize: 12.5, color: '#b91c1c' }}>{saveError}</span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* ── Sentry control + workstations (V3: no enrollment) ──────────────── */
/*
 * Workstations already run MNMON — there is nothing to install or enroll.
 * The account-wide switch below writes the BadgeOverlay block of the
 * customer's MNMON config (PUT /api/portal/badge/overlay-config); the MNMON
 * config-flow delivers it to every workstation. The device list is read-only
 * and doubles as the registration-console options for scan-to-register.
 */

// Overlay config may arrive flat, nested, or MNMON-cased — normalize.
const normalizeOverlayConfig = (d) => {
  const raw = (d && (d.config || d.overlay_config)) || d || {};
  return {
    enabled: Boolean(raw.enabled != null ? raw.enabled : raw.Enabled),
    mode: raw.mode || raw.Mode || 'flow',
    idle_timeout_seconds:
      raw.idle_timeout_seconds != null ? raw.idle_timeout_seconds
        : raw.IdleTimeoutSeconds != null ? raw.IdleTimeoutSeconds : 300,
  };
};

// Per-device overlay state, if the backend includes it (tolerant keys).
const wsOverlayEnabled = (w) => {
  const v = w.overlay_enabled != null ? w.overlay_enabled
    : w.overlay_effective_enabled != null ? w.overlay_effective_enabled
      : (w.overlay && w.overlay.enabled) != null ? w.overlay.enabled
        : (w.badge_overlay && w.badge_overlay.enabled) != null ? w.badge_overlay.enabled
          : (w.effective_overlay && w.effective_overlay.enabled) != null ? w.effective_overlay.enabled
            : null;
  return typeof v === 'boolean' ? v : null;
};

function ToggleSwitch({ on, onToggle, disabled }) {
  return (
    <button
      onClick={onToggle}
      disabled={disabled}
      role="switch"
      aria-checked={on}
      style={{
        position: 'relative', width: 44, height: 24, borderRadius: 999,
        border: 'none', cursor: disabled ? 'default' : 'pointer', padding: 0,
        background: on ? '#068dac' : '#d1d5db', transition: 'background .15s',
        opacity: disabled ? 0.6 : 1, flexShrink: 0,
      }}
    >
      <span style={{
        position: 'absolute', top: 3, left: on ? 23 : 3, width: 18, height: 18,
        borderRadius: 999, background: '#fff', transition: 'left .15s',
        boxShadow: '0 1px 2px rgba(0,0,0,.25)',
      }} />
    </button>
  );
}

function SentryControlSection() {
  const [state, setState] = useState('loading'); // loading | ready | error
  const [error, setError] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [mode, setMode] = useState('flow');
  const [timeoutSec, setTimeoutSec] = useState(300);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  const [saveError, setSaveError] = useState(null);

  // Emergency (glass-break) code — write-only: we only ever know whether one
  // is set (glass_break_set), never the code itself.
  const [glassBreakSet, setGlassBreakSet] = useState(false);
  const [gbCode, setGbCode] = useState('');
  const [gbState, setGbState] = useState('idle'); // idle | saving | removing | saved | removed | error
  const [gbError, setGbError] = useState(null);

  const load = useCallback(async () => {
    setState('loading');
    try {
      const d = await badgeApi.getOverlayConfig();
      const cfg = normalizeOverlayConfig(d);
      setEnabled(cfg.enabled);
      setMode(cfg.mode);
      setTimeoutSec(cfg.idle_timeout_seconds);
      setGlassBreakSet(Boolean(d && d.glass_break_set));
      setState('ready');
    } catch (e) {
      setError(e.message || 'Failed to load Sentry settings.');
      setState('error');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const t = parseInt(timeoutSec, 10);
    if (Number.isNaN(t) || t < 30 || t > 3600) {
      setSaveState('error');
      setSaveError('Idle timeout must be between 30 and 3600 seconds.');
      return;
    }
    setSaveState('saving');
    setSaveError(null);
    try {
      const d = await badgeApi.putOverlayConfig({
        enabled, mode, idle_timeout_seconds: t,
      });
      if (d && (d.config || d.overlay_config || d.enabled != null || d.Enabled != null)) {
        const cfg = normalizeOverlayConfig(d);
        setEnabled(cfg.enabled);
        setMode(cfg.mode);
        setTimeoutSec(cfg.idle_timeout_seconds);
      }
      setSaveState('saved');
      setTimeout(() => setSaveState((s) => (s === 'saved' ? 'idle' : s)), 2500);
    } catch (e) {
      setSaveState('error');
      setSaveError(e.message || 'Failed to save Sentry settings.');
    }
  };

  const saveGlassBreak = async () => {
    const code = gbCode.trim();
    if (code.length < 4 || code.length > 64) {
      setGbState('error');
      setGbError('The emergency code must be 4–64 characters.');
      return;
    }
    setGbState('saving');
    setGbError(null);
    try {
      await badgeApi.setGlassBreakCode(code);
      setGbCode('');
      setGlassBreakSet(true);
      setGbState('saved');
      setTimeout(() => setGbState((s) => (s === 'saved' ? 'idle' : s)), 2500);
    } catch (e) {
      setGbState('error');
      setGbError(e.message || 'Failed to set the emergency access code.');
    }
  };

  const removeGlassBreak = async () => {
    setGbState('removing');
    setGbError(null);
    try {
      await badgeApi.removeGlassBreakCode();
      setGlassBreakSet(false);
      setGbState('removed');
      setTimeout(() => setGbState((s) => (s === 'removed' ? 'idle' : s)), 2500);
    } catch (e) {
      setGbState('error');
      setGbError(e.message || 'Failed to remove the emergency access code.');
    }
  };

  return (
    <div style={{ ...cardStyle, marginBottom: 14 }}>
      {sectionHeading(Power, 'Sentry on your workstations')}
      <div style={{ fontSize: 12.5, color: '#888', margin: '-6px 0 12px' }}>
        One switch for your whole account. Your workstations are already
        managed by Macula — turning Sentry on delivers the badge overlay to
        them automatically, nothing to install.
      </div>

      {state === 'loading' && <SectionLoading />}
      {state === 'error' && <SectionError message={error} onRetry={load} />}
      {state === 'ready' && (
        <>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
            borderRadius: 8, marginBottom: 14,
            border: enabled ? '1.5px solid #068dac' : '1px solid #eee',
            background: enabled ? '#f0f9fb' : '#fafafa',
          }}>
            <ToggleSwitch on={enabled} onToggle={() => setEnabled((v) => !v)} disabled={saveState === 'saving'} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#333' }}>
                Sentry {enabled ? 'on' : 'off'}
              </div>
              <div style={{ fontSize: 12.5, color: '#777' }}>
                {enabled
                  ? 'The badge overlay runs on every workstation.'
                  : 'The badge overlay is off everywhere — workstations behave normally.'}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
            {Object.entries(MODE_INFO).map(([key, info]) => (
              <label
                key={key}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
                  padding: '10px 12px', borderRadius: 8,
                  border: mode === key ? '1.5px solid #068dac' : '1px solid #eee',
                  background: mode === key ? '#f0f9fb' : '#fff',
                  opacity: enabled ? 1 : 0.6,
                }}
              >
                <input
                  type="radio"
                  name="sentry-overlay-mode"
                  checked={mode === key}
                  onChange={() => setMode(key)}
                  style={{ marginTop: 3, accentColor: '#068dac' }}
                />
                <span>
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#333', display: 'block' }}>
                    {info.label}
                  </span>
                  <span style={{ fontSize: 12.5, color: '#777', lineHeight: 1.5 }}>{info.hint}</span>
                </span>
              </label>
            ))}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 14, opacity: enabled ? 1 : 0.6 }}>
            <Timer size={16} color="#068dac" />
            <span style={{ fontSize: 13.5, color: '#333', fontWeight: 600 }}>Idle timeout</span>
            <input
              type="number"
              min={30}
              max={3600}
              step={30}
              value={timeoutSec}
              onChange={(e) => setTimeoutSec(e.target.value)}
              style={{ ...inputStyle, width: 90 }}
            />
            <span style={{ fontSize: 12.5, color: '#888' }}>
              seconds (30–3600) with no activity before the screen re-locks
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              onClick={save}
              disabled={saveState === 'saving'}
              style={{ ...primaryBtn, opacity: saveState === 'saving' ? 0.7 : 1 }}
            >
              {saveState === 'saving' ? <Loader2 size={13} className="spinner" /> : <Check size={13} />}
              Save settings
            </button>
            {saveState === 'saved' && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, color: '#16a34a', fontWeight: 600 }}>
                <CheckCircle size={13} />
                Saved — workstations pick it up automatically.
              </span>
            )}
            {saveState === 'error' && saveError && (
              <span style={{ fontSize: 12.5, color: '#b91c1c' }}>{saveError}</span>
            )}
          </div>

          <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid #f2f2f2' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <Siren size={15} color="#068dac" />
              <span style={{ fontSize: 13.5, fontWeight: 600, color: '#333' }}>
                Emergency access code
              </span>
              <Chip
                label={glassBreakSet ? 'Set' : 'Not set'}
                {...(glassBreakSet ? GREEN : GRAY)}
              />
            </div>
            <div style={{ fontSize: 12.5, color: '#888', marginBottom: 10, lineHeight: 1.5 }}>
              Staff can use this code to unlock a workstation in an emergency
              (e.g. a failed badge reader). Every use is recorded to the
              compliance log.
            </div>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <input
                type="password"
                value={gbCode}
                onChange={(e) => setGbCode(e.target.value)}
                placeholder={glassBreakSet ? 'Enter a new code to replace it' : 'Emergency code (4–64 characters)'}
                autoComplete="new-password"
                maxLength={64}
                style={{ ...inputStyle, width: 240 }}
              />
              <button
                onClick={saveGlassBreak}
                disabled={gbState === 'saving' || gbState === 'removing' || !gbCode.trim()}
                style={{
                  ...primaryBtn,
                  opacity: (gbState === 'saving' || gbState === 'removing' || !gbCode.trim()) ? 0.6 : 1,
                }}
              >
                {gbState === 'saving' ? <Loader2 size={13} className="spinner" /> : <KeyRound size={13} />}
                {glassBreakSet ? 'Replace code' : 'Save code'}
              </button>
              {glassBreakSet && (
                <ConfirmButton
                  label="Remove"
                  confirmLabel="Confirm — disables emergency code"
                  busy={gbState === 'removing'}
                  onConfirm={removeGlassBreak}
                />
              )}
            </div>
            {gbState === 'saved' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12.5, color: '#16a34a', fontWeight: 600, marginTop: 8 }}>
                <CheckCircle size={13} />
                Emergency code set — workstations pick it up automatically.
              </div>
            )}
            {gbState === 'removed' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12.5, color: '#16a34a', fontWeight: 600, marginTop: 8 }}>
                <CheckCircle size={13} />
                Emergency code removed — glass-break is disabled on your workstations.
              </div>
            )}
            {gbState === 'error' && gbError && (
              <div style={{ fontSize: 12.5, color: '#b91c1c', marginTop: 8 }}>{gbError}</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function WorkstationsSection({ workstations, state, error, onReload }) {
  return (
    <div style={{ ...cardStyle, marginBottom: 14 }}>
      {sectionHeading(Monitor, 'Workstations')}
      <div style={{ fontSize: 12.5, color: '#888', margin: '-6px 0 12px' }}>
        Your Macula-managed workstations. Any of them can be the registration
        console when you add an employee or register a card.
      </div>

      {state === 'loading' && <SectionLoading />}
      {state === 'error' && <SectionError message={error} onRetry={onReload} />}
      {state === 'ready' && (
        workstations.length === 0 ? (
          <EmptyNote>
            No workstations found. Devices managed by Macula appear here
            automatically — contact us if this list should not be empty.
          </EmptyNote>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Workstation</th>
                  <th style={thStyle}>Sentry</th>
                  <th style={thStyle}>Last seen</th>
                </tr>
              </thead>
              <tbody>
                {workstations.map((w) => {
                  const online = w.last_seen && (Date.now() - Date.parse(w.last_seen)) < 5 * 60000;
                  const overlay = wsOverlayEnabled(w);
                  return (
                    <tr key={wsId(w)}>
                      <td style={{ ...tdStyle, fontWeight: 600 }}>{w.hostname || '—'}</td>
                      <td style={tdStyle}>
                        {overlay === null
                          ? <span style={{ color: '#aaa' }}>—</span>
                          : <Chip label={overlay ? 'On' : 'Off'} {...(overlay ? GREEN : GRAY)} />}
                      </td>
                      <td style={tdStyle}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <span style={{
                            width: 7, height: 7, borderRadius: 999, flexShrink: 0,
                            background: online ? '#16a34a' : '#d1d5db',
                          }} />
                          {relTime(w.last_seen)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}

/* ── Attribution ────────────────────────────────────────────────────── */

const ENTRY_METHOD = {
  badge: { label: 'Badge', ...GREEN },
  pin: { label: 'PIN', ...GRAY },
  break_glass: { label: 'Break-glass', ...RED },
};

function AttributionSection() {
  const [state, setState] = useState('loading');
  const [error, setError] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const load = useCallback(async (from, to) => {
    setState('loading');
    try {
      const filters = { limit: 200 };
      if (from) filters.from = `${from}T00:00:00`;
      if (to) filters.to = `${to}T23:59:59`;
      const d = await badgeApi.listSessions(filters);
      setSessions(Array.isArray(d.sessions) ? d.sessions : []);
      setState('ready');
    } catch (e) {
      setError(e.message || 'Failed to load sessions.');
      setState('error');
    }
  }, []);

  useEffect(() => { load('', ''); }, [load]);

  return (
    <div style={{ ...cardStyle, marginBottom: 14 }}>
      {sectionHeading(Clock, 'Who was where')}
      <div style={{ fontSize: 12.5, color: '#888', margin: '-6px 0 12px' }}>
        Every badge session — who was at which workstation, from when to when.
        This is the attribution trail your HIPAA evidence binder draws from.
      </div>

      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        <label style={{ fontSize: 12.5, color: '#666' }}>
          From{' '}
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{ ...inputStyle, padding: '6px 8px' }}
          />
        </label>
        <label style={{ fontSize: 12.5, color: '#666' }}>
          To{' '}
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={{ ...inputStyle, padding: '6px 8px' }}
          />
        </label>
        <button onClick={() => load(fromDate, toDate)} style={outlineBtn}>
          Apply
        </button>
        {(fromDate || toDate) && (
          <button
            onClick={() => { setFromDate(''); setToDate(''); load('', ''); }}
            style={{ ...outlineBtn, border: '1px solid #ccc', color: '#666' }}
          >
            Clear
          </button>
        )}
      </div>

      {state === 'loading' && <SectionLoading />}
      {state === 'error' && <SectionError message={error} onRetry={() => load(fromDate, toDate)} />}
      {state === 'ready' && (
        sessions.length === 0 ? (
          <EmptyNote>
            No sessions in this range yet — they appear as soon as staff start
            badging in.
          </EmptyNote>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Person</th>
                  <th style={thStyle}>Workstation</th>
                  <th style={thStyle}>From</th>
                  <th style={thStyle}>To</th>
                  <th style={thStyle}>Entry</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => {
                  const em = ENTRY_METHOD[s.entry_method] || { label: s.entry_method || '—', ...GRAY };
                  return (
                    <tr key={s.id}>
                      <td style={{ ...tdStyle, fontWeight: 600 }}>{personName(s)}</td>
                      <td style={tdStyle}>{s.hostname || '—'}</td>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{fmtDateTime(s.session_start)}</td>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                        {s.session_end ? fmtDateTime(s.session_end) : (
                          <Chip label="Active now" {...GREEN} />
                        )}
                      </td>
                      <td style={tdStyle}><Chip label={em.label} color={em.color} bg={em.bg} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}

function AlertsSection() {
  const [state, setState] = useState('loading');
  const [error, setError] = useState(null);
  const [breakGlass, setBreakGlass] = useState([]);
  const [sharing, setSharing] = useState([]);

  const load = useCallback(async () => {
    setState('loading');
    try {
      const [bg, sa] = await Promise.all([
        badgeApi.listBreakGlass(),
        badgeApi.listSharingAlerts(),
      ]);
      setBreakGlass(Array.isArray(bg.break_glass_sessions) ? bg.break_glass_sessions : []);
      setSharing(Array.isArray(sa.sharing_alerts) ? sa.sharing_alerts : []);
      setState('ready');
    } catch (e) {
      setError(e.message || 'Failed to load alerts.');
      setState('error');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const warnCard = {
    ...cardStyle,
    border: '1px solid #f0d9a8',
    background: '#fffbeb',
  };

  return (
    <div style={{ ...warnCard, marginBottom: 14 }}>
      {sectionHeading(AlertTriangle, 'Needs your attention')}
      <div style={{ fontSize: 12.5, color: '#8a6d3b', margin: '-6px 0 12px' }}>
        Break-glass entries and suspected card sharing. Both are logged in the
        evidence trail — review them and follow up with your staff.
      </div>

      {state === 'loading' && <SectionLoading />}
      {state === 'error' && <SectionError message={error} onRetry={load} />}
      {state === 'ready' && (
        <>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#8a6d3b', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
            Break-glass entries
          </div>
          {breakGlass.length === 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#16a34a', fontWeight: 600, marginBottom: 14 }}>
              <CheckCircle size={14} />
              None — nobody has used emergency access.
            </div>
          ) : (
            <div style={{ marginBottom: 14 }}>
              {breakGlass.map((b, i) => (
                <div
                  key={b.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                    padding: '8px 0', borderTop: i === 0 ? 'none' : '1px solid #f3e8c8',
                  }}
                >
                  <Siren size={15} color="#b91c1c" style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: 13, color: '#333' }}>
                    <strong>{personName(b)}</strong> used break-glass on{' '}
                    <strong>{b.hostname || 'unknown workstation'}</strong>
                    {' — '}{fmtDateTime(b.session_start)}
                    {b.session_end ? ` to ${fmtDateTime(b.session_end)}` : ' (still open)'}
                  </span>
                </div>
              ))}
            </div>
          )}

          <div style={{ fontSize: 12, fontWeight: 700, color: '#8a6d3b', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
            Suspected card sharing
          </div>
          {sharing.length === 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#16a34a', fontWeight: 600 }}>
              <CheckCircle size={14} />
              None detected.
            </div>
          ) : (
            <div>
              {sharing.map((a, i) => (
                <div
                  key={a.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                    padding: '8px 0', borderTop: i === 0 ? 'none' : '1px solid #f3e8c8',
                  }}
                >
                  <AlertTriangle size={15} color="#b45309" style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: 13, color: '#333' }}>
                    <strong>{personName(a)}</strong>'s card tapped at{' '}
                    <strong>{a.hostname || 'unknown workstation'}</strong> at{' '}
                    {fmtDateTime(a.occurred_at)} — too soon after use elsewhere.
                    The same card can't be in two places.
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ── ACTIVE: dashboard ──────────────────────────────────────────────── */

function BadgeDashboard({ data }) {
  // Persons + cards are loaded together here because the Cards section
  // needs the roster for holder names.
  const [rosterState, setRosterState] = useState('loading');
  const [rosterError, setRosterError] = useState(null);
  const [persons, setPersons] = useState([]);
  const [cards, setCards] = useState([]);

  // Workstations (the customer's MNMON devices) are lifted here because the
  // scan-to-register modals in Roster and Cards need them as the
  // registration-console options, and the Workstations section lists them.
  const [wsState, setWsState] = useState('loading');
  const [wsError, setWsError] = useState(null);
  const [workstations, setWorkstations] = useState([]);

  const loadRoster = useCallback(async () => {
    setRosterState('loading');
    try {
      const [p, c] = await Promise.all([badgeApi.listPersons(), badgeApi.listCards()]);
      setPersons(Array.isArray(p.persons) ? p.persons : []);
      setCards(Array.isArray(c.cards) ? c.cards : []);
      setRosterState('ready');
    } catch (e) {
      setRosterError(e.message || 'Failed to load roster.');
      setRosterState('error');
    }
  }, []);

  const loadWorkstations = useCallback(async () => {
    setWsState('loading');
    try {
      const d = await badgeApi.listWorkstations();
      setWorkstations(Array.isArray(d.workstations) ? d.workstations : []);
      setWsState('ready');
    } catch (e) {
      setWsError(e.message || 'Failed to load workstations.');
      setWsState('error');
    }
  }, []);

  useEffect(() => { loadRoster(); loadWorkstations(); }, [loadRoster, loadWorkstations]);

  return (
    <>
      <h1 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 4px' }}>Sentry</h1>
      <p style={{ color: '#777', fontSize: 14, margin: '0 0 20px' }}>
        {data.service_label || 'Manage your staff badges, workstations, and the who-was-where trail.'}
      </p>

      <AlertsSection />
      <RosterSection
        persons={persons}
        state={rosterState}
        error={rosterError}
        onReload={loadRoster}
        workstations={workstations}
        wsState={wsState}
      />
      <CardsSection
        cards={cards}
        persons={persons}
        state={rosterState}
        error={rosterError}
        onReload={loadRoster}
        workstations={workstations}
        wsState={wsState}
      />
      <SentryControlSection />
      <WorkstationsSection
        workstations={workstations}
        state={wsState}
        error={wsError}
        onReload={loadWorkstations}
      />
      <PolicySection />
      <AttributionSection />
    </>
  );
}

/* ── INACTIVE: sales pitch ──────────────────────────────────────────── */

const PITCH_ITEMS = [
  {
    icon: Fingerprint,
    title: 'Tap in, tap out',
    body: 'Staff badge in at any workstation and are working in seconds — no shared passwords, no sticky notes on the monitor.',
  },
  {
    icon: Lock,
    title: 'Screens that lock themselves',
    body: 'Walk away and the screen locks on its own. Nobody has to remember Win+L, and no patient chart is ever left open at the front desk.',
  },
  {
    icon: Users,
    title: 'Know who was at every machine',
    body: 'A complete who-was-where trail: which person, which workstation, from when to when. Shared logins stop being a black hole.',
  },
  {
    icon: Siren,
    title: 'Emergency access with accountability',
    body: 'Break-glass entry keeps care moving in an emergency — and every use is flagged for review, so access never goes unexplained.',
  },
  {
    icon: ShieldCheck,
    title: 'HIPAA evidence, automatically',
    body: 'Every session lands in your tamper-evident compliance trail. When an auditor asks who had access to that workstation, you have the answer.',
  },
];

function BadgePitch() {
  const [ctaState, setCtaState] = useState('idle'); // idle | sending | sent | error

  const handleInterest = async () => {
    setCtaState('sending');
    try {
      await badgeApi.expressInterest();
      setCtaState('sent');
    } catch (e) {
      setCtaState('error');
    }
  };

  return (
    <>
      <div style={{ textAlign: 'center', padding: '18px 0 26px' }}>
        <Fingerprint size={44} color="#068dac" style={{ marginBottom: 14 }} />
        <h1 style={{ fontSize: 24, fontWeight: 700, margin: '0 0 12px' }}>Sentry</h1>
        <p style={{ fontSize: 17, color: '#333', lineHeight: 1.5, margin: '0 auto 10px', maxWidth: 560, fontWeight: 500 }}>
          One tap of a badge, and you always know who was at every workstation.
        </p>
        <p style={{ fontSize: 14, color: '#777', lineHeight: 1.6, margin: '0 auto', maxWidth: 560 }}>
          Shared workstations don't have to mean shared accountability. Our
          badge service puts a tap-to-unlock reader on each machine: screens
          lock themselves when staff walk away, and every session is recorded —
          who, where, from when to when — straight into your compliance trail.
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 26 }}>
        {PITCH_ITEMS.map((item) => {
          const Icon = item.icon;
          return (
            <div key={item.title} style={{ ...cardStyle, display: 'flex', gap: 14, alignItems: 'flex-start' }}>
              <Icon size={22} color="#068dac" style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 2 }}>{item.title}</div>
                <div style={{ fontSize: 13.5, color: '#777', lineHeight: 1.55 }}>{item.body}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ ...cardStyle, textAlign: 'center', padding: '26px 18px' }}>
        {ctaState === 'sent' ? (
          <>
            <FileCheck2 size={32} color="#16a34a" style={{ marginBottom: 10 }} />
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>Thanks — we'll be in touch.</div>
            <p style={{ fontSize: 13.5, color: '#777', margin: 0 }}>
              Someone from Macula will reach out shortly to walk you through
              what badge-in looks like at your practice.
            </p>
          </>
        ) : (
          <>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
              Want to see it on one of your workstations?
            </div>
            <p style={{ fontSize: 13.5, color: '#777', margin: '0 0 16px' }}>
              No commitment — we'll demo the tap-to-unlock flow and show you the
              attribution trail it builds.
            </p>
            <button
              onClick={handleInterest}
              disabled={ctaState === 'sending'}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: '#068dac', color: '#fff', border: 'none', borderRadius: 8,
                padding: '12px 24px', fontSize: 14, fontWeight: 600,
                cursor: ctaState === 'sending' ? 'default' : 'pointer',
                opacity: ctaState === 'sending' ? 0.7 : 1,
              }}
            >
              {ctaState === 'sending' && <Loader2 size={15} className="spinner" />}
              I'm interested — talk to us
            </button>
            {ctaState === 'error' && (
              <div style={{ fontSize: 13, color: '#c0392b', marginTop: 10 }}>
                Something went wrong sending your request — please try again,
                or just give us a call.
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

/* ── PAGE ───────────────────────────────────────────────────────────── */

/**
 * Badge page — visible to every portal customer.
 * Active badge service -> the self-service dashboard (roster, cards,
 * policy, workstations, attribution).
 * No active service -> pitch for Sentry.
 */
export default function Badge() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await badgeApi.getStatus();
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setError('Could not load badge service information.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '24px 16px' }}>
      {loading && (
        <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>
          <Loader2 className="spinner-large" size={36} />
          <div style={{ marginTop: 10 }}>Loading…</div>
        </div>
      )}

      {!loading && error && (
        <div style={{ padding: 30, color: '#c0392b' }}>{error}</div>
      )}

      {!loading && !error && data && (
        data.active ? <BadgeDashboard data={data} /> : <BadgePitch />
      )}
    </div>
  );
}
